import { Component, computed, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbilityService, rxResourceFixed } from '@zayan-erp/shared';
import { FormControl, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { InputFieldComponent, CurrencyInputFieldComponent } from '@zayan-erp/shared';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { LinkedRealtorAgentToLot } from 'libs/sales/src/lib/models/linked-realtor-agent.models';
import { SalesService } from 'libs/sales/src/lib/services/sales.service';
import { of, tap } from 'rxjs';

@Component({
  selector: 'lib-linked-realtor-agents-form',
  imports: [InputFieldComponent, FormsModule, ReactiveFormsModule, ToggleSwitchModule, CurrencyInputFieldComponent],
  templateUrl: './linked-realtor-agents-form.component.html'
})
export class LinkedRealtorAgentsFormComponent {
  #destroyRef = inject(DestroyRef);
  #salesService = inject(SalesService);
  #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  realtorAgentId = input<string | null>(null);
  dealId = input<string | null>(null);
  reloadTrigger = output<void>();

  realtorAgentForm = new FormGroup({
    allowCommissionOnBase: new FormControl<boolean | null>(null),
    allowCommissionOnOption: new FormControl<boolean | null>(null),
    baseCommissionPercentage: new FormControl<number | null>(null, [Validators.min(0)]),
    baseCommissionAmount: new FormControl<number | null>(null, [Validators.min(0)]),
    optionCommissionPercentage: new FormControl<number | null>(null, [Validators.min(0)]),
    optionCommissionAmount: new FormControl<number | null>(null, [Validators.min(0)]),
    includeInContract: new FormControl<boolean | null>(null),
    notes: new FormControl<string | null>(null)
  });

  linkedRealtorAgentResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId(), realtorAgentId: this.realtorAgentId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.dealId && params.realtorAgentId) {
        return this.#salesService.getLinkedRealtorAgent(params.dealId, params.realtorAgentId).pipe(
          tap(linkedRealtorAgent => {
            this.editForm(linkedRealtorAgent);
          })
        );
      }
      return of(null);
    }
  });
  editForm(linkedRealtorAgent: LinkedRealtorAgentToLot): void {
    this.realtorAgentForm.patchValue({
      allowCommissionOnBase: linkedRealtorAgent.allowCommissionOnBase,
      allowCommissionOnOption: linkedRealtorAgent.allowCommissionOnOption,
      baseCommissionPercentage: linkedRealtorAgent.baseCommissionPercentage,
      baseCommissionAmount: linkedRealtorAgent.baseCommissionAmount,
      optionCommissionPercentage: linkedRealtorAgent.optionCommissionPercentage,
      optionCommissionAmount: linkedRealtorAgent.optionCommissionAmount,
      includeInContract: linkedRealtorAgent.includeInContract,
      notes: linkedRealtorAgent.notes
    });

    if (this.canWriteDeals()) {
      this.realtorAgentForm.enable({ emitEvent: false });
    } else {
      this.realtorAgentForm.disable({ emitEvent: false });
    }
  }
  onSubmit(): void {
    if (!this.canWriteDeals()) return;
    if (this.realtorAgentForm.invalid) {
      return;
    }
    const realtorAgent = {
      allowCommissionOnBase: this.realtorAgentForm.value.allowCommissionOnBase,
      allowCommissionOnOption: this.realtorAgentForm.value.allowCommissionOnOption,
      baseCommissionPercentage: this.realtorAgentForm.value.baseCommissionPercentage,
      baseCommissionAmount: this.realtorAgentForm.value.baseCommissionAmount,
      optionCommissionPercentage: this.realtorAgentForm.value.optionCommissionPercentage,
      optionCommissionAmount: this.realtorAgentForm.value.optionCommissionAmount,
      includeInContract: this.realtorAgentForm.value.includeInContract,
      notes: this.realtorAgentForm.value.notes
    } as LinkedRealtorAgentToLot;
    this.#salesService
      .updateLinkedRealtorAgent(this.dealId() ?? '', this.realtorAgentId() ?? '', realtorAgent)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.realtorAgentForm.reset();
        this.reloadTrigger.emit();
      });
  }
}
